import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import AudioUploader from './AudioUploader';
import TextUploader from './TextUploader';
import AnalysisResult from './AnalysisResult';
import { Button } from '@/components/ui/button';
import type { AnalysisResult as AnalysisResultType } from '@/types/analysis';

interface AudioAnalyzerProps {
  onSignOut: () => void;
}

const AudioAnalyzer = ({ onSignOut }: AudioAnalyzerProps) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResultType | null>(null);
  const [uploadType, setUploadType] = useState<'audio' | 'text'>('audio');

  const handleAnalysis = async (file?: File) => {
    console.log('Starting analysis...', { isTest: !file, uploadType });
    setIsAnalyzing(true);
    try {
      let requestBody: any = {
        isTest: !file
      };

      if (file) {
        if (uploadType === 'audio') {
          const base64Audio = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
          requestBody.audio = base64Audio;
        } else {
          const formData = new FormData();
          formData.append('file', file);
          
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('text_documents')
            .upload(`${crypto.randomUUID()}-${file.name}`, file);

          if (uploadError) {
            throw new Error('Failed to upload text document');
          }

          requestBody.textDocumentPath = uploadData.path;
        }
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }
      requestBody.userId = user.id;

      console.log('Sending request to analyze-sales-call function...');
      const { data, error } = await supabase.functions.invoke('analyze-sales-call', {
        body: requestBody
      });

      if (error) {
        console.error('Edge function error:', error);
        throw error;
      }

      console.log('Analysis received:', data);
      setAnalysisResult({
        transcript: data.transcript,
        feedback: data.feedback
      });
      
      toast.success('Analysis complete!');
    } catch (error: any) {
      console.error('Analysis error:', error);
      const errorMessage = error.message || 'Failed to analyze the file. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleAudioUpload = (file: File) => {
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB limit
    
    if (file.size > MAX_FILE_SIZE) {
      toast.error('File size must be less than 10MB');
      return;
    }

    console.log('Starting audio upload and analysis...', { fileSize: file.size });
    handleAnalysis(file);
  };

  const handleTextUpload = (file: File) => {
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB limit
    
    if (file.size > MAX_FILE_SIZE) {
      toast.error('File size must be less than 5MB');
      return;
    }

    console.log('Starting text upload and analysis...', { fileSize: file.size });
    handleAnalysis(file);
  };

  const handleTestAnalysis = () => {
    handleAnalysis();
  };

  return (
    <div className="absolute inset-0 w-full min-h-screen overflow-auto bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="w-full min-h-screen px-4 py-20">
        <div className="max-w-4xl mx-auto space-y-8">
          <div className="text-center space-y-4">
            <h1 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-violet-600">
              Sales Call Analyzer
            </h1>
            <p className="text-lg text-gray-600 px-4 max-w-2xl mx-auto">
              Upload your sales call recording or text transcript and get instant AI feedback 
              based on your sales performance and areas of improvement
            </p>
          </div>

          {!analysisResult && (
            <div className="space-y-6 p-8 mx-4 bg-white/50 backdrop-blur-sm rounded-xl border border-gray-100 shadow-sm">
              <div className="flex justify-center gap-4">
                <Button
                  variant={uploadType === 'audio' ? 'default' : 'outline'}
                  onClick={() => setUploadType('audio')}
                  className="transition-all duration-300 hover:scale-105"
                >
                  Audio Upload
                </Button>
                <Button
                  variant={uploadType === 'text' ? 'default' : 'outline'}
                  onClick={() => setUploadType('text')}
                  className="transition-all duration-300 hover:scale-105"
                >
                  Text Upload
                </Button>
              </div>
              
              <div className="bg-white/80 backdrop-blur-sm rounded-lg p-6 transition-all duration-300 hover:shadow-md">
                {uploadType === 'audio' ? (
                  <AudioUploader onAudioSelected={handleAudioUpload} />
                ) : (
                  <TextUploader onTextSelected={handleTextUpload} />
                )}
              </div>
              
              <div className="text-center">
                <Button 
                  variant="secondary"
                  onClick={handleTestAnalysis}
                  disabled={isAnalyzing}
                  className="transition-all duration-300 hover:scale-105"
                >
                  Run Test Analysis
                </Button>
              </div>
            </div>
          )}

          {isAnalyzing && (
            <div className="flex flex-col items-center gap-4 p-8 mx-4 bg-white/50 backdrop-blur-sm rounded-xl animate-fade-in">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <p className="text-gray-600">Analyzing your sales call...</p>
            </div>
          )}

          {analysisResult && !isAnalyzing && (
            <div className="space-y-6 mx-4 animate-fade-in">
              <button
                onClick={() => setAnalysisResult(null)}
                className="text-primary hover:text-primary/80 font-medium transition-colors"
              >
                ← Analyze another call
              </button>
              <div className="bg-white/50 backdrop-blur-sm rounded-xl border border-gray-100 shadow-sm">
                <AnalysisResult
                  transcript={analysisResult.transcript}
                  feedback={analysisResult.feedback}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AudioAnalyzer;